'use es6';

import { createSelector } from 'reselect';
import { sortBy, isEmpty } from 'underscore';
import { PropertyObjectTypes } from 'FormsUI/constants/PropertyObjectTypes';
import { ContactProperties, ContactPropertiesOrder, TicketPropertiesOrder } from 'forms-utils-lib/constants/Properties';
import { getFormFieldsByPropertyId, getFormLanguage } from 'FormsUI/selectors/FormDefinition';
import { getPropertyByObjectTypeIdMap } from 'FormsUI/utils/CustomObjects';
import { isSupportedProperty } from 'FormsUI/utils/PropertiesUtils';
import { getWithOriginalAndUpdatedLabel } from 'FormsUI/utils/FormTranslationUtils';
import { getObjectTypeIdValue } from 'FormsCommonComponents/form-definition/utils/FormFieldUtils';
import { isContactProperty, isTicketProperty } from 'FormsCommonComponents/utils/PropertyObjectTypes';
function propertyMatchesSearchQuery(property, searchQuery = '') {
  const lowerCaseSearchQuery = searchQuery.toLowerCase();
  return property.label.toLowerCase().indexOf(lowerCaseSearchQuery) !== -1 || property.name.toLowerCase().indexOf(lowerCaseSearchQuery) !== -1;
}
export function getPropertyGroupsById(state) {
  return state.properties.propertyGroups;
}
export function getContactPropertyGroupsByGroupName(state) {
  return Object.values(state.properties.propertyGroups || {}).filter(pg => pg.groupObjectType === PropertyObjectTypes.CONTACT).reduce((memo, pg) => Object.assign({}, memo, {
    [pg.name]: pg
  }), {});
}
export function getPropertiesById(state) {
  return state.properties.properties;
}
const getContactPropertiesById = state => {
  return Object.values(state.properties.properties || {}).filter(p => isContactProperty(getObjectTypeIdValue(p.objectTypeId, p.propertyObjectType))).reduce((memo, p) => Object.assign({}, memo, {
    [p.id]: p
  }), {});
};
export function getPropertiesForForm(state) {
  return getPropertyByObjectTypeIdMap(state.customObjects);
}
const getTicketPropertiesById = state => {
  return Object.values(state.properties.properties || {}).filter(p => isTicketProperty(getObjectTypeIdValue(p.objectTypeId, p.propertyObjectType))).reduce((memo, p) => Object.assign({}, memo, {
    [p.id]: p
  }), {});
};
export const getTicketProperties = createSelector([getTicketPropertiesById, getFormFieldsByPropertyId, getFormLanguage], (ticketPropertiesById, formFieldsByPropertyId, formLanguage) => {
  const byTicketProperties = p => isTicketProperty(getObjectTypeIdValue(p.objectTypeId, p.propertyObjectType)) && !p.externalOptions;
  const withOriginalAndUpdatedLabel = getWithOriginalAndUpdatedLabel(formFieldsByPropertyId, formLanguage);
  const ticketProperties = Object.values(ticketPropertiesById || {}).filter(byTicketProperties).map(withOriginalAndUpdatedLabel);
  const sortedTicketProperties = sortBy(ticketProperties, p => TicketPropertiesOrder[p.name]);
  return sortedTicketProperties;
});
export function getSearchQuery(state) {
  return state.properties.searchQuery;
}
const getPropertyGroups = createSelector([getPropertiesById, getPropertyGroupsById], (propertiesById, propertyGroupsById) => {
  const propertyGroups = Object.values(propertyGroupsById || {});
  const sortedPropertyGroups = sortBy(propertyGroups, propertyGroup => propertyGroup.displayOrder);
  return sortedPropertyGroups.map(propertyGroup => Object.assign({}, propertyGroup, {
    properties: propertyGroup.properties.map(property => propertiesById[property])
  }));
});
export const getDefaultContactProperties = createSelector([getContactPropertiesById, getSearchQuery, getFormFieldsByPropertyId, getFormLanguage], (contactPropertiesById, searchQuery, formFieldsByPropertyId, formLanguage) => {
  const byDefaultContactProperties = contactProperty => {
    return Object.prototype.hasOwnProperty.call(ContactProperties, contactProperty.name) ? propertyMatchesSearchQuery(contactProperty, searchQuery) : false;
  };
  const withOriginalAndUpdatedLabel = getWithOriginalAndUpdatedLabel(formFieldsByPropertyId, formLanguage);
  const defaultContactProperties = Object.values(contactPropertiesById || {}).filter(byDefaultContactProperties).map(withOriginalAndUpdatedLabel);
  return sortBy(defaultContactProperties, contactProperty => ContactPropertiesOrder[contactProperty.name]);
});
export function getAllProperties(state) {
  const properties = Object.values(getPropertiesById(state) || {});
  return sortBy(properties, property => property.displayOrder);
}
export function getProperty(state, propertyName) {
  return getPropertiesById(state)[propertyName] || {};
}
export const getPropertyGroupsForSearchQuery = createSelector([getPropertyGroups, getSearchQuery], (propertyGroups, searchQuery) => {
  return propertyGroups.map(propertyGroup => {
    return Object.assign({}, propertyGroup, {
      properties: sortBy(propertyGroup.properties.filter(property => {
        return isSupportedProperty(property) && propertyMatchesSearchQuery(property, searchQuery);
      }), property => property.label.toLowerCase())
    });
  }).filter(propertyGroup => !isEmpty(propertyGroup.properties));
});
export function getContactPropertyGroupsForSearchQuery(state) {
  return getPropertyGroupsForSearchQuery(state).filter(pg => pg.groupObjectType === PropertyObjectTypes.CONTACT);
}
export function getTicketPropertyGroupsForSearchQuery(state) {
  return getPropertyGroupsForSearchQuery(state).filter(pg => pg.groupObjectType === PropertyObjectTypes.TICKET);
}
export function getPropertiesRequestState(state) {
  return state.properties.requestState;
}